/**
 * How a workspace has configured the Big Job Agent, as one editable form.
 *
 * The panel is one form with one Save button, and the API is shaped to match:
 * `mcp_servers` replaces the whole list, so a row left out is a server deleted.
 * That makes the draft below the *desired end state* rather than a set of
 * changes, which is why removing a row does nothing until Save.
 */
import type { AgentMcpServer, AgentSettings, AgentSettingsRequest } from '@/api/types'

/** What the API accepts, mirrored so somebody is stopped before a round trip. */
export const AGENT_INSTRUCTIONS_MAX = 8000

/** One MCP server as it is being edited. */
export interface McpServerDraft {
  /** Absent for a row that has not been created yet. */
  id?: number
  /**
   * A stable identity for the row while it is on screen.
   *
   * Not the id: a new row has none, and keying by array index makes React
   * reuse the box somebody is typing in when a row above it is removed.
   */
  key: string
  name: string
  url: string
  /** What was typed. Never what is stored -- a stored token is never returned. */
  token: string
  /** Whether the server has a token at rest, which is all the API will say. */
  hasToken: boolean
  /** Whether the stored token should be removed. See `serverBody`. */
  clearToken: boolean
  enabled: boolean
}

export interface AgentDraft {
  service: string
  alias: string
  model: string
  instructions: string
  servers: McpServerDraft[]
}

let nextKey = 0

export function newServer(): McpServerDraft {
  nextKey += 1
  return {
    key: `new-${String(nextKey)}`,
    name: '',
    url: '',
    token: '',
    hasToken: false,
    clearToken: false,
    // Added by somebody who wants it used; switching it off is a later decision.
    enabled: true,
  }
}

export function draftFrom(settings: AgentSettings): AgentDraft {
  return {
    service: settings.llm_service ?? '',
    alias: settings.llm_connection_alias ?? '',
    model: settings.llm_model ?? '',
    instructions: settings.additional_instructions ?? '',
    servers: (settings.mcp_servers ?? []).map((server: AgentMcpServer) => ({
      // A conditional spread rather than `id: server.id`, because "present and
      // undefined" is a different type from absent here -- and absent is what
      // "this row has not been created yet" means to `serverBody`.
      ...(server.id === undefined ? {} : { id: server.id }),
      key: `stored-${String(server.id ?? 0)}`,
      name: server.name,
      url: server.url,
      token: '',
      hasToken: server.has_token,
      clearToken: false,
      enabled: server.enabled ?? true,
    })),
  }
}

/**
 * A connection is named by a *pair*, and the select needs one value.
 *
 * Two services can each have a connection called `default`, so neither half
 * identifies one on its own. Joined on a colon, and split from the left,
 * because a service id has no colon in it and an alias might.
 */
export function connectionKey(service: string, alias: string): string {
  return `${service}:${alias}`
}

export function parseConnectionKey(key: string): { service: string; alias: string } {
  const at = key.indexOf(':')
  if (at === -1) return { service: key, alias: '' }
  return { service: key.slice(0, at), alias: key.slice(at + 1) }
}

/**
 * What to send for one server.
 *
 * The token is the only asymmetric field, and it has three cases rather than
 * two: something typed replaces the stored token, an explicit clear removes it,
 * and an untouched box leaves it alone. The third is the reason the key is
 * *omitted* rather than sent empty -- an empty string is how the API is told to
 * clear one, so sending it for every unchanged row would wipe every token in
 * the list the first time somebody renamed a server.
 */
export function serverBody(draft: McpServerDraft): Omit<AgentMcpServer, 'has_token'> {
  const base = {
    ...(draft.id === undefined ? {} : { id: draft.id }),
    name: draft.name.trim(),
    url: draft.url.trim(),
    enabled: draft.enabled,
  }

  if (draft.token !== '') return { ...base, token: draft.token }
  if (draft.clearToken) return { ...base, token: '' }
  return base
}

export function agentBody(draft: AgentDraft): AgentSettingsRequest {
  return {
    llm_service: draft.service,
    llm_connection_alias: draft.alias,
    llm_model: draft.model.trim(),
    additional_instructions: draft.instructions,
    mcp_servers: draft.servers.map(serverBody),
  }
}

export function instructionsTooLong(instructions: string): boolean {
  return instructions.length > AGENT_INSTRUCTIONS_MAX
}

/**
 * The refusals the backend reported against individual server rows.
 *
 * Pulled apart here so a bad name is shown on the row that caused it rather
 * than as one opaque sentence above a list of five. The shape is DRF's, as
 * Baserow renders it: `mcp_servers` is an array positionally matching what was
 * sent, each entry a map of field name to a list of messages, and a row with
 * nothing wrong with it is an empty object rather than a hole.
 *
 * @param detail The `detail` of an `ERROR_REQUEST_BODY_VALIDATION`.
 * @return Row index to a message, for the rows that were refused.
 */
export function serverErrorsFrom(detail: unknown): Record<number, string> {
  const rows = (detail as { mcp_servers?: unknown } | null)?.mcp_servers
  if (!Array.isArray(rows)) return {}

  const errors: Record<number, string> = {}
  rows.forEach((row, index) => {
    if (row === null || typeof row !== 'object') return
    const messages = Object.values(row as Record<string, unknown>)
      .flatMap((value: unknown): unknown[] => (Array.isArray(value) ? value : [value]))
      .map((message: unknown) =>
        typeof message === 'string'
          ? message
          : (message as { error?: unknown } | null)?.error,
      )
      .filter((message): message is string => typeof message === 'string')

    if (messages.length > 0) errors[index] = messages.join(' ')
  })
  return errors
}
